/**
 * Rich-text handling for the linktone fields (song lyrics + credits).
 *
 * Those fields are filled by artists in the linktone generator, and are rendered
 * with v-html on the linktone page — white text over the blurred artwork. Text
 * pasted from Word / another site drags its original look along (background
 * colours, font colours, sizes, bold), which shows up as black or white boxes and
 * random bold over the artwork. Until now the only way out was to paste through
 * Notepad and back.
 *
 * Two complementary guards live here:
 *   forcePlainPaste()  — the editor side: a paste drops its formatting on the way in.
 *   cleanRichText()    — the display side: styling already stored in the database
 *                        is neutralised when the linktone is rendered.
 *
 * Neither is a security sanitiser: they only drop presentation. Event handlers,
 * javascript: URLs and <iframe>/<img> survive, exactly as they did before. Anything
 * guarding against XSS on these fields still has to be added separately.
 */

/** Inline CSS that describes layout rather than looks, and is safe to keep. */
const KEEP_STYLE_PROPS = ['text-align', 'direction'];

/** Removed together with their contents — these only ever carry pasted markup noise. */
const DROP_ELEMENTS = ['style', 'script', 'meta', 'link', 'base', 'title'];

/** Attributes whose only job is to paint the text. */
const DROP_ATTRIBUTES = ['class', 'bgcolor', 'color', 'face', 'background'];

/**
 * Quill keeps a second, selector-based matcher for these tags on top of the generic
 * element pass (<b> → bold, <i> → italic). That one runs later, so the generic pass
 * alone would let a paste whose outermost node is a bare <b> keep its bold.
 */
const ALIASED_TAGS = ['b', 'i', 'strong', 'em'];

/**
 * Registers clipboard matchers that reduce pasted content to plain text — the same
 * result as the manual "paste into Notepad, copy back" workaround. Line and paragraph
 * breaks survive; colours, backgrounds, fonts, sizes, bold and pasted images do not.
 * Formatting stays available through the toolbar.
 *
 * Custom matchers run after Quill's built-in ones, so this sees (and drops) the
 * attributes matchAttributor/matchStyles derived from the source markup.
 *
 * @param {object} quill the Quill instance, from <QuillEditor @ready="..." />
 */
export function forcePlainPaste(quill) {
  if (!quill || !quill.clipboard) return;

  const stripFormatting = (node, delta) => {
    const Delta = delta.constructor;
    return delta.ops.reduce((plain, op) => {
      // Embeds (images, video) arrive as an object insert — they have no place in lyrics.
      if (typeof op.insert !== 'string') return plain;
      return plain.insert(op.insert);
    }, new Delta());
  };

  quill.clipboard.addMatcher(Node.ELEMENT_NODE, stripFormatting);
  quill.clipboard.addMatcher(Node.TEXT_NODE, stripFormatting);
  ALIASED_TAGS.forEach((tag) => quill.clipboard.addMatcher(tag, stripFormatting));
}

function keptStyle(value) {
  return String(value)
    .split(';')
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const prop = declaration.split(':')[0].trim().toLowerCase();
      return declaration.indexOf(':') !== -1 && KEEP_STYLE_PROPS.indexOf(prop) !== -1;
    })
    .join('; ');
}

// A linktone re-renders on every play/pause and like, so the same handful of values is
// cleaned over and over. Remembering the last few results keeps that off the render path.
const cache = new Map();
const CACHE_LIMIT = 200;

function clean(raw) {
  const doc = new window.DOMParser().parseFromString(raw, 'text/html');

  DROP_ELEMENTS.forEach((tag) => {
    const nodes = doc.getElementsByTagName(tag);
    // Live collection — walk backwards so removing an entry doesn't skip the next.
    for (let i = nodes.length - 1; i >= 0; i--) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
  });

  const elements = doc.body.getElementsByTagName('*');
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    DROP_ATTRIBUTES.forEach((attr) => el.removeAttribute(attr));
    if (el.tagName === 'FONT') el.removeAttribute('size');

    if (el.hasAttribute('style')) {
      const style = keptStyle(el.getAttribute('style'));
      if (style) el.setAttribute('style', style);
      else el.removeAttribute('style');
    }
  }

  return doc.body.innerHTML;
}

/**
 * Strips the pasted-in look from stored lyrics/credits HTML, leaving the text and
 * its structure untouched so it inherits the linktone page's own typography.
 * Content is never dropped — on anything unexpected the original value is returned.
 *
 * Presentation only — see the module note above, this is not an XSS sanitiser.
 *
 * @param {string} html value as stored in the database
 * @returns {string} the same content, without the presentational leftovers
 */
export function cleanRichText(html) {
  if (html === null || html === undefined || html === '') return '';

  const raw = String(html);
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return raw;
  if (cache.has(raw)) return cache.get(raw);

  let cleaned;
  try {
    cleaned = clean(raw);
  } catch (e) {
    // A malformed value must never blank out a linktone — show it as it was.
    cleaned = raw;
  }

  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(raw, cleaned);
  return cleaned;
}
